import { isEqual } from 'lodash-es';
import isbot from 'isbot';
import { BehaviorSubject, map, share } from 'rxjs';

import { Inject, Injectable, InjectionToken } from '@angular/core';

import { Deployment, Environment } from '@bp/shared/models/core';
import { IAppVersion, IEnvironmentConfig, IRepositorySummary, Platform } from '@bp/shared/typings';
import { getLocationMainDomain } from '@bp/shared/utilities/core';

import { LocalBackendState } from '@bp/frontend/services/persistent-state-keepers';
import { extractDevEnvironmentSlotName } from '@bp/frontend/utilities/common';

export const PLATFORM = new InjectionToken<Platform>('PLATFORM');

@Injectable({
	providedIn: 'root',
})
export class EnvironmentService implements IEnvironmentConfig {

	private static __environment: IEnvironmentConfig;

	private static __mainDomain: string;

	private static __checkoutMainDomain: string;

	private static __checkoutOrigin: string;

	private static __checkoutLauncherUrl: string;

	private static __bridgerAdminOrigin: string;

	private static __merchantAdminOrigin: string;

	private static readonly __devEnvironmentSlotName = extractDevEnvironmentSlotName(location.hostname);

	private static readonly __isDevEnvironment = !!EnvironmentService.__devEnvironmentSlotName;

	static isHumanManaged = false;

	static init(environment: IEnvironmentConfig): void {
		this.__environment = environment;

		this.__mainDomain = getLocationMainDomain();

		this.__checkoutMainDomain = environment.isProduction ? 'bridgerpay.com' : this.__mainDomain;

		this.isHumanManaged = this.__isHumanManaged();

		this.__setCheckoutLoaderUrlAccordingToHostDomain();

		this.__setCheckoutOriginAccordingToHostDomain();

		this.__setBridgerAdminOriginAccordingToHostDomain();

		this.__setMerchantAdminOriginAccordingToHostDomain();
	}

	private static __setCheckoutLoaderUrlAccordingToHostDomain(): void {
		EnvironmentService.__checkoutLauncherUrl = EnvironmentService.__environment.checkoutLauncherUrl
			.replace('{{mainDomain}}', EnvironmentService.__checkoutMainDomain);

		if (EnvironmentService.__isDevEnvironment)
			EnvironmentService.__checkoutLauncherUrl = EnvironmentService.__checkoutLauncherUrl.replace('checkout', `checkout-${ EnvironmentService.__devEnvironmentSlotName }`);
	}

	private static __setCheckoutOriginAccordingToHostDomain(): void {
		EnvironmentService.__checkoutOrigin = EnvironmentService.__environment.checkoutOrigin
			.replace('{{mainDomain}}', EnvironmentService.__checkoutMainDomain);

		if (EnvironmentService.__isDevEnvironment)
			EnvironmentService.__checkoutOrigin = EnvironmentService.__checkoutOrigin.replace('checkout', `checkout-${ EnvironmentService.__devEnvironmentSlotName }`);
	}

	private static __setBridgerAdminOriginAccordingToHostDomain(): void {
		EnvironmentService.__bridgerAdminOrigin = EnvironmentService.__environment.bridgerAdminOrigin;

		if (EnvironmentService.__isDevEnvironment)
			EnvironmentService.__bridgerAdminOrigin = EnvironmentService.__bridgerAdminOrigin.replace('console', `console-${ EnvironmentService.__devEnvironmentSlotName }`);
	}

	private static __setMerchantAdminOriginAccordingToHostDomain(): void {
		EnvironmentService.__merchantAdminOrigin = EnvironmentService.__environment.merchantAdminOrigin;

		if (EnvironmentService.__isDevEnvironment)
			EnvironmentService.__merchantAdminOrigin = EnvironmentService.__merchantAdminOrigin.replace('dash', `dash-${ EnvironmentService.__devEnvironmentSlotName }`);
	}

	private static __isHumanManaged(): boolean {
		return !isbot(navigator.userAgent);
	}

	private readonly __environment = EnvironmentService.__environment;

	readonly api = this.__environment.api;

	readonly apiVersion = this.__environment.apiVersion;

	readonly appVersion = this.__environment.appVersion;

	readonly appVersionLiteral = 'fe-app-version';

	readonly repositorySummary = this.__environment.repositorySummary;

	readonly mainDomain = EnvironmentService.__mainDomain;

	readonly checkoutOrigin = EnvironmentService.__checkoutOrigin;

	readonly checkoutLauncherUrl = EnvironmentService.__checkoutLauncherUrl;

	readonly bridgerAdminOrigin = EnvironmentService.__bridgerAdminOrigin;

	readonly merchantAdminOrigin = EnvironmentService.__merchantAdminOrigin;

	readonly signupOrigin = this.__environment.signupOrigin;

	readonly name = this.__environment.name;

	readonly environment = Environment.parseStrict(this.name);

	readonly isLocal = this.environment.isLocal;

	readonly isDeployed = this.__environment.isDeployed;

	readonly isStaging = this.environment.isStaging;

	readonly isPullRequestPreview = this.environment.isPullRequestPreview;

	readonly isPrerelease = this.environment.isPrerelease;

	readonly isProduction = this.environment.isProduction;

	readonly isNotProduction = !this.isProduction;

	readonly isStagingOrPreview = this.isStaging || this.isPullRequestPreview;

	readonly isDeployedStagingOrProduction = (this.isStaging || this.isProduction) && !LocalBackendState.isActive;

	readonly isBackoffice = location.hostname.includes('backoffice');

	readonly devEnvironmentSlotName = EnvironmentService.__devEnvironmentSlotName;

	readonly isDevEnvironment = EnvironmentService.__isDevEnvironment;

	private readonly __repositorySummariesPerDeployment$ = new BehaviorSubject<Map<Deployment, IRepositorySummary>>(new Map([
		[ Deployment.frontend, this.repositorySummary ],
	]));

	readonly repositorySummariesPerDeployment$ = this.__repositorySummariesPerDeployment$.asObservable();

	readonly backendDeploymentVersions$ = this.__repositorySummariesPerDeployment$.pipe(
		map(summaryPerDeployment => Object.fromEntries([ ...summaryPerDeployment
			.entries() ]
			.filter(([ deployment ]) => deployment.isBackend)
			.map(([ deployment, summary ]) => [ `${ deployment }BackendVersion`, summary.releaseTitle ]))),
		share(),
	);

	readonly realUserMonitoring = this.__environment.realUserMonitoring;

	// #region Third Party Keys

	readonly sentry = this.__environment.sentry;

	readonly intercom = this.__environment.intercom;

	readonly logrocket = this.__environment.logrocket;

	readonly googleTagAnalyticsId = this.__environment.googleTagAnalyticsId;

	readonly googleTagTransportUrl = this.__environment.googleTagTransportUrl;

	readonly oauthGoogleClientId = this.__environment.oauthGoogleClientId;

	readonly cloudflareTurnstileSiteKey = this.__environment.cloudflareTurnstileSiteKey;

	readonly aiExpertsChatWidgetUrl = this.__environment.aiExpertsChatWidgetUrl;

	// #endregion

	constructor(@Inject(PLATFORM) public readonly platform: Platform) {
		document.documentElement.classList.add(this.environment.name);
	}

	setDeploymentRepositorySummary(deployment: Deployment, summary: IRepositorySummary): void {
		if (isEqual(summary, this.__repositorySummariesPerDeployment$.value.get(deployment)))
			return;

		this.__repositorySummariesPerDeployment$.next(
			new Map(this.__repositorySummariesPerDeployment$.value)
				.set(deployment, summary),
		);
	}

	parseAppVersion(appVersion: string): IAppVersion {
		const segments = appVersion.split('-');
		const [ release ] = segments;
		const shortSHA = segments.at(-1) ?? null;
		const releaseName = segments.slice(1, -1).join('-');

		return { release, releaseName, shortSHA, releaseTitle: appVersion };
	}

}
